import { computed } from 'vue'
import { useTablesStore } from '@/stores/tables'
import { useUserStore } from '@/stores/user'
import { activeLanguages, baseLang, FALLBACK_LANG } from '@/core/helpers/contentLang'

/**
 * Inhaltssprache des Studios.
 *
 * Quelle ist die persönliche Einstellung des angemeldeten Nutzers
 * (`user.settings.language`, gepflegt unter /account/settings). Ist dort nichts
 * gesetzt oder existiert die Sprache im Management nicht (mehr), gilt die
 * Basissprache aus mgmt_languages — die Sprache, in der Rechtstexte und
 * Vorlagen tatsächlich geschrieben sind.
 *
 * Bewusst KEIN Fallback auf navigator.language: welche Sprachen gepflegt sind,
 * entscheidet das Management, nicht der Browser. Eine Vorlage in einer nie
 * gepflegten Sprache anzuzeigen hieße, überall leere Felder zu zeigen.
 *
 * `Userdata.datasource.id` liegt im user-Store, nicht im auth-Store.
 */
export function useContentLang() {
  const tablesStore = useTablesStore()
  const userStore   = useUserStore()

  const languages = computed(() => activeLanguages((tablesStore.tables as any)?.mgmt_languages))
  const base      = computed(() => baseLang((tablesStore.tables as any)?.mgmt_languages))

  const userId = computed(() => (userStore as any)?.Userdata?.datasource?.id || '')
  const currentUser = computed(() =>
    (((tablesStore.tables as any)?.user as any[]) || []).find((u: any) => u.id === userId.value) || null
  )

  const lang = computed<string>(() => {
    let stored: any = null
    try {
      const raw = currentUser.value?.settings
      stored = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null
    } catch {
      stored = null
    }
    const wanted = stored?.language
    if (!wanted) return base.value || FALLBACK_LANG
    // Nur Sprachen zulassen, die es im Management wirklich gibt. Solange die
    // Sprachliste noch nicht gesynct ist, die Einstellung akzeptieren —
    // der Fallback in tField/localize greift ohnehin pro Feld.
    if (!languages.value.length) return wanted
    return languages.value.some(l => l.code === wanted) ? wanted : (base.value || FALLBACK_LANG)
  })

  return { lang, base, languages }
}
